import {
  Box,
  Chip,
  TextField,
} from "@mui/material";
import React, { useState, useMemo } from "react";
import ReusableDataTable from "../../Component/DataTable/ReusableDataTablePrime";
import { FilterMatchMode } from "primereact/api";
import { useDispatch, useSelector } from "react-redux";
import { setLoading } from "../../store/CommonSlice";
import { CustomMessage } from "../../const/Spinner";
import { enqueueSnackbar } from "notistack";
import ReusablePrimeButton from "../../Component/Buttons/ReusablePrimeButton";
import AddIcon from "@mui/icons-material/Add";
import { ownerAssetMaster } from "../../types/assetTypes";
import {
  getAssetOwnerById,
  getOwnerAssetMasterDataList,
  saveAssetOwner,
  deleteOwner
} from "../../api/Services/masterServie";
import {
  getOwnerAssetColumns,
} from "../../const/AssertData";
import ReusablePrimeReactDialog from "../../Component/Dialog/ReusablePrimeReactDialog";
import Swal from "sweetalert2";
import { RootState } from "../../store/Reduxstore";

interface AssetOwnerProps {
  setPathname: (path: string) => void;
}

const AssetOwner: React.FC = () => {
  const dispatch = useDispatch();
  const [data, setData] = useState<ownerAssetMaster[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    rows: 13,
    totalRecords: 0,
  });
  const [openDialog, setOpenDialog] = useState(false);
  const [assetOwnerData, setAssetOwnerData] = useState<ownerAssetMaster>({
    assetOwnerId: 0,
    assetOwnerName: "",
  });

  // Memoized column definition
  const columns = useMemo(() => getOwnerAssetColumns(), []);

  if (columns.filter((val) => val.field === "action").length === 0) {
    columns.push({
      field: "action",
      header: "Action",
      sortable: false,
      filter: false,
      filterField: "action",
      filterPlaceholder: "Search",
      style: { minWidth: "12rem" },
      body: (rowData: any) => (
        <>
          <ReusablePrimeButton
            //<ReusablePrimeButton
            icon="pi pi-pencil"
            className="p-button-rounded p-button-outlined"
            onClick={() => handleEditFamilyAsset(rowData.assetOwnerId)}
          />

        </>
      ),
    });
  }

  const [filterData, setFilterData] = useState<Record<string, any>>(() =>
    (columns ?? []).reduce((acc, col) => {
      if (col.field) {
        acc[col.field] = { value: "", matchMode: "startsWith" }; // Ensure correct structure
      }
      return acc;
    }, {} as Record<string, { value: string; matchMode: string }>),
  );

  const handleEditFamilyAsset = async (assetOwnerId: number) => {
    dispatch(setLoading(true));
    try {
      const response = await getAssetOwnerById(assetOwnerId);
      let { data, status } = response;
      if (status.code === 200 && data) {
        setAssetOwnerData(data);
        setOpenDialog(true);
      } else {
        CustomMessage(status.message, "error", enqueueSnackbar);
      }
    } catch (error: any) {
      CustomMessage(error.message, "error", enqueueSnackbar);
    } finally {
      dispatch(setLoading(false));
    }
  };
  const userDetails = useSelector((state: RootState) => state.auth)
  const handleDeleteOwner = async (assetOwnerId: number) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won’t be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        dispatch(setLoading(true));
        try {
          const response = await deleteOwner(assetOwnerId);
          if (response.status === 200) {
            onFetchData(PAGESTART, PAGESEND, filterData);


            CustomMessage("Data deleted", "success", enqueueSnackbar);
          } else {
            CustomMessage(
              "Failed to delete the owner.",
              "error",
              enqueueSnackbar
            );
          }
        } catch (error: any) {
          CustomMessage(
            error?.message || "Something went wrong!",
            "error",
            enqueueSnackbar
          );
        } finally {
          dispatch(setLoading(false));
        }
      } else {
        CustomMessage("Deletion cancelled", "info", enqueueSnackbar);
      }
    });
  };

  // Generate filters dynamically


  const onFetchData = async (page: number, rows: number, filter?: Record<string, any>) => {
    dispatch(setLoading(true));
    try {
      const { data, status, pagination } = await getOwnerAssetMasterDataList(page, rows, filter ?? filterData);
      if (status.code === 200 && data?.length > 0) {
        setData(data);
        setPagination({
          page: pagination.currentPage,
          rows: pagination.limit,
          totalRecords: pagination.totalCount,
        });
      } else {
        setData([]);
        CustomMessage(status.message, "error", enqueueSnackbar);
      }
    } catch (error: any) {
      CustomMessage(error?.message, "error", enqueueSnackbar);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.target;
    setAssetOwnerData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleClear = () => {
    setOpenDialog(false);
    setAssetOwnerData({
      assetOwnerId: 0,
      assetOwnerName: "",
    });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event?.preventDefault();
    if (assetOwnerData.assetOwnerName === "") {
      CustomMessage("Add the name", "error", enqueueSnackbar);
      return;
    }
    dispatch(setLoading(true));
    try {
      const response = await saveAssetOwner(assetOwnerData);
      let { status } = response;
      if (status.code === 200 || status.code === 201) {
        CustomMessage(status.message, "success", enqueueSnackbar);
        handleClear();
        onFetchData(PAGESTART, PAGESEND, filterData);
      } else {
        CustomMessage(status.message, "error", enqueueSnackbar);
      }
    } catch (error: any) {
      CustomMessage(error?.message, "error", enqueueSnackbar);
    } finally {
      dispatch(setLoading(false));
    }
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end"
        }}
      >
        {userDetails.role.some(
          r => r.roleName === "Admin" || r.roleName === "Assets List Write"
        ) && (
            <Chip
              className="btn-black-outlined mb-3"
              label="New"
              color="warning"
              onClick={() => setOpenDialog(true)}
              icon={<AddIcon />}
              variant="outlined"
            />
          )}
      </div>
      <Box
        sx={{
          backgroundColor: "#f9f9f9",
          borderRadius: 3,
          boxShadow: 3,
          padding: 2,
        }}
      >
        <ReusableDataTable
          data={data}
          columns={columns}
          onFetchData={onFetchData}
          emptyMessage="No data found."
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          selectionMode="multiple"
          rowsPerPageOptions={[5, 10, 25, 50]}
          //filters={filters}
          pagination={pagination}
          setPagination={setPagination}
          filters={filterData}
          setFilters={setFilterData}
          onRowDoubleClick={(rowData) => {
            console.log('Double-click row:', rowData);
            handleEditFamilyAsset(rowData.assetOwnerId);
          }}
        />
      </Box>
      <ReusablePrimeReactDialog
        header="Add Asset Owner"
        visible={openDialog}
        onHide={handleClear}
      >
        <form >
          <h6>Asset Owner Name</h6>
          <TextField
            label=""
            fullWidth
            variant="outlined"
            value={assetOwnerData.assetOwnerName}
            name="assetOwnerName"
            onChange={handleChange}
            sx={{ mt: 0 }}
            required
          />
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: "30px"
          }}>
            <ReusablePrimeButton
              onClick={handleClear}
              severity="contrast"
              title="Cancel"

            >
              Cancel
            </ReusablePrimeButton>
            <ReusablePrimeButton className="ms-1" severity="info" onClick={handleSubmit} allowedRoles={["Admin", "Assets List Write"]}>
              Submit
            </ReusablePrimeButton>
          </div>
        </form>
      </ReusablePrimeReactDialog>
    </>
  );
};

export default AssetOwner;
